import { computeClockDistance, isBeyondClockThreshold, metersToFeet } from "@taskhealth/shared";
import { LinearGradient } from "expo-linear-gradient";
import { useEffect, useRef, useState } from "react";
import {
    View,
    Text,
    StyleSheet,
    Pressable,
    Modal,
    ActivityIndicator,
    ScrollView,
    TextInput,
    useWindowDimensions
} from "react-native";

import { useKeyboardOffset } from "@/hooks/useKeyboardOffset";
import type { FormDefinition } from "@/lib/api";
import {
    captureLocation,
    locationBlockCopy,
    openLocationSettings,
    type ClockLocation,
    type LocationBlockReason
} from "@/lib/capture-location";
import { colors, fonts, spacing, radius, shadows, gradients } from "@/lib/theme";

// Clock-in payload. lat/lng are optional: when the device can't get a GPS fix
// the nurse proceeds with a typed `locationNote` reason and no coordinates.
type ClockInPayload = Partial<ClockLocation> & { locationNote?: string };

/** Room kept above a keyboard-lifted sheet for the status bar / notch. */
const TOP_GUTTER = 56;
/** Floor for the sheet on a short screen — below this, the content scrolls. */
const MIN_SHEET_HEIGHT = 260;

interface ClockInModalProps {
    visible: boolean;
    visit: FormDefinition["visit"];
    patient: NonNullable<FormDefinition["patient"]>;
    onConfirm: (location?: ClockInPayload) => Promise<void> | void;
    onCancel: () => void;
}

export function ClockInModal({ visible, visit, patient, onConfirm, onCancel }: ClockInModalProps) {
    const [loading, setLoading] = useState(false);
    const [gpsStatus, setGpsStatus] = useState<"idle" | "locating" | "found" | "denied">("idle");
    // Steps: normal confirm; a "you're far — why?" step (GPS present but beyond
    // the threshold) which now REQUIRES a reason; and a "no GPS" step that also
    // requires one. Both block, because both are the only chance to capture her
    // account of a clock reading that can later hold her pay.
    const [step, setStep] = useState<"confirm" | "far" | "noGps">("confirm");
    const [captured, setCaptured] = useState<ClockLocation | undefined>(undefined);
    const [farDistance, setFarDistance] = useState<number | null>(null);
    const [note, setNote] = useState("");
    // Why the no-GPS step was reached — decides retry-vs-open-settings + copy.
    const [blockReason, setBlockReason] = useState<LocationBlockReason>("no_fix");
    // Live accuracy of the best reading so far, in feet. The capture spends up
    // to CLOCK_SETTLE_MS improving the fix; showing it tighten is what keeps
    // that from reading as a hung button.
    const [gpsAccuracyFt, setGpsAccuracyFt] = useState<number | null>(null);

    const reset = () => {
        setStep("confirm");
        setNote("");
        setGpsStatus("idle");
        setGpsAccuracyFt(null);
    };

    const runCapture = async () => {
        setLoading(true);
        setGpsStatus("locating");
        setGpsAccuracyFt(null);
        const cap = await captureLocation({
            onProgress: (accuracyM) => {
                if (accuracyM == null || accuracyM <= 0) {
                    return;
                }
                const feet = Math.round(metersToFeet(accuracyM));
                setGpsAccuracyFt((best) => (best == null || feet < best ? feet : best));
            }
        });

        // No GPS fix (permission off, Location Services off, or a failed read) →
        // block on the "no GPS" step. It re-prompts the OS when it still can, and
        // otherwise offers Settings or a typed reason; we never clock her in
        // silently with no coordinates. `blockReason` tailors that step.
        if (!cap.location) {
            setBlockReason(cap.reason);
            setGpsStatus("denied");
            setStep("noGps");
            setLoading(false);
            return;
        }
        const location = cap.location;

        setGpsStatus("found");

        if (patient.lat != null && patient.lng != null) {
            const distanceInput = {
                nurse: { lat: location.lat, lng: location.lng },
                patient: { lat: patient.lat, lng: patient.lng },
                accuracyFeet: location.accuracy != null ? metersToFeet(location.accuracy) : null
            };
            const result = computeClockDistance(distanceInput);
            // `isBeyondClockThreshold`, NOT `result.isFar`. isFar goes quiet past
            // a mile (its "that's a bad fix" cap), which meant a nurse 1.7 mi from
            // the patient saw no prompt at all — and then had her pay held days
            // later for exactly that distance, having never been asked. The
            // warning and the pay hold must fire on the same rule.
            if (isBeyondClockThreshold(distanceInput)) {
                setCaptured(location);
                setFarDistance(result.distanceFeet);
                setStep("far");
                setLoading(false);
                return;
            }
        }

        await onConfirm(location);
        setLoading(false);
        setGpsStatus("idle");
    };

    const confirmAnyway = async () => {
        setLoading(true);
        const trimmed = note.trim();
        await onConfirm({ ...(captured as ClockLocation), locationNote: trimmed || undefined });
        setLoading(false);
        reset();
    };

    // "no GPS" step: proceed with the required reason and no coordinates.
    const confirmNoGps = async () => {
        const trimmed = note.trim();
        if (trimmed === "") {
            return;
        }
        setLoading(true);
        await onConfirm({ locationNote: trimmed });
        setLoading(false);
        reset();
    };

    const recheck = () => {
        setNote("");
        setStep("confirm");
        void runCapture();
    };

    const handleCancel = () => {
        reset();
        onCancel();
    };

    const now = new Date();
    const tz = visit.timezone ?? "America/New_York";
    const currentTime = now.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: tz
    });

    const noGpsCopy = locationBlockCopy(blockReason, "in");

    // Both blocking steps here require typing, and this sheet sits where the
    // keyboard opens. See the matching note in ClockOutModal: without the lift,
    // the reason field and the button below it are buried under the keyboard
    // with no way to get it back down.
    const keyboardOffset = useKeyboardOffset();
    const { height: windowHeight } = useWindowDimensions();
    const maxSheetHeight = Math.max(MIN_SHEET_HEIGHT, windowHeight - keyboardOffset - TOP_GUTTER);
    const scrollRef = useRef<ScrollView>(null);
    useEffect(() => {
        if (keyboardOffset > 0) {
            scrollRef.current?.scrollToEnd({ animated: true });
        }
    }, [keyboardOffset]);

    return (
        <Modal visible={visible} transparent animationType="slide" onRequestClose={handleCancel}>
            <View style={styles.backdrop}>
                <Pressable style={styles.backdropTouch} onPress={handleCancel} />
                <View
                    style={[
                        styles.sheet,
                        { maxHeight: maxSheetHeight },
                        keyboardOffset > 0 && { marginBottom: keyboardOffset }
                    ]}
                >
                    <View style={styles.handle} />

                    <ScrollView
                        ref={scrollRef}
                        // "handled" so the first tap on the clock-in button clocks
                        // her in instead of being eaten to dismiss the keyboard.
                        keyboardShouldPersistTaps="handled"
                        showsVerticalScrollIndicator={false}
                        contentContainerStyle={styles.sheetContent}
                    >
                        {step === "far" ? (
                            <>
                                <Text style={styles.title}>You seem far from the patient</Text>
                                {/* The reason is REQUIRED here, and it did not use to
                                be. As an optional field it collected 3 answers in
                                90 days across 276 far clock events, which left the
                                nurse with nothing on the record when the visit was
                                later questioned. One line now saves her a week's
                                wait later. Blocking matches the no-GPS step below,
                                which has always required a reason. */}
                                <Text style={styles.farMessage}>
                                    Your location looks about{" "}
                                    <Text style={styles.farDistance}>
                                        {farDistance != null ? `${farDistance} ft` : "far"}
                                    </Text>{" "}
                                    from {patient.firstName}&apos;s address. Tell us why and you can
                                    still clock in — this is what we check against later, so a short
                                    note now keeps your pay moving.
                                </Text>

                                <TextInput
                                    style={styles.noteInput}
                                    placeholder="e.g. Met the patient outside / address is slightly off"
                                    placeholderTextColor={colors.textTertiary}
                                    value={note}
                                    onChangeText={setNote}
                                    multiline
                                    maxLength={2000}
                                    editable={!loading}
                                />

                                <Pressable
                                    onPress={confirmAnyway}
                                    disabled={loading || note.trim() === ""}
                                    style={({ pressed }) => [pressed && styles.pressed]}
                                >
                                    <LinearGradient
                                        colors={gradients.teal}
                                        start={{ x: 0, y: 0 }}
                                        end={{ x: 1, y: 1 }}
                                        style={[
                                            styles.confirmButton,
                                            shadows.teal,
                                            note.trim() === "" && styles.confirmButtonDisabled
                                        ]}
                                    >
                                        {loading ? (
                                            <ActivityIndicator color="#FFFFFF" />
                                        ) : (
                                            <Text style={styles.confirmText}>CLOCK IN</Text>
                                        )}
                                    </LinearGradient>
                                </Pressable>

                                <Pressable
                                    onPress={recheck}
                                    disabled={loading}
                                    style={styles.cancelButton}
                                >
                                    <Text style={styles.cancelText}>Re-check my location</Text>
                                </Pressable>
                            </>
                        ) : step === "noGps" ? (
                            <>
                                <Text style={styles.title}>{noGpsCopy.title}</Text>
                                <Text style={styles.farMessage}>{noGpsCopy.message}</Text>

                                {noGpsCopy.showSettings ? (
                                    // Permission is denied for good (or Location Services are off) —
                                    // the OS won't re-prompt, so a plain Retry can't help. Deep-link
                                    // to Settings, then let her retry once she's back.
                                    <>
                                        <Pressable
                                            onPress={openLocationSettings}
                                            disabled={loading}
                                            style={({ pressed }) => [pressed && styles.pressed]}
                                        >
                                            <LinearGradient
                                                colors={gradients.teal}
                                                start={{ x: 0, y: 0 }}
                                                end={{ x: 1, y: 1 }}
                                                style={[styles.confirmButton, shadows.teal]}
                                            >
                                                <Text style={styles.confirmText}>
                                                    OPEN PHONE SETTINGS
                                                </Text>
                                            </LinearGradient>
                                        </Pressable>
                                        <Pressable
                                            onPress={() => void runCapture()}
                                            disabled={loading}
                                            style={styles.cancelButton}
                                        >
                                            <Text style={styles.cancelText}>
                                                {loading ? "Checking…" : "I turned it on — retry"}
                                            </Text>
                                        </Pressable>
                                    </>
                                ) : (
                                    <Pressable
                                        onPress={() => void runCapture()}
                                        disabled={loading}
                                        style={({ pressed }) => [pressed && styles.pressed]}
                                    >
                                        <LinearGradient
                                            colors={gradients.teal}
                                            start={{ x: 0, y: 0 }}
                                            end={{ x: 1, y: 1 }}
                                            style={[styles.confirmButton, shadows.teal]}
                                        >
                                            {loading ? (
                                                <ActivityIndicator color="#FFFFFF" />
                                            ) : (
                                                <Text style={styles.confirmText}>
                                                    RETRY LOCATION
                                                </Text>
                                            )}
                                        </LinearGradient>
                                    </Pressable>
                                )}

                                <Text style={styles.noGpsOrLabel}>
                                    or clock in without location
                                </Text>
                                <TextInput
                                    style={styles.noteInput}
                                    placeholder="Reason (required) — e.g. no signal in the building"
                                    placeholderTextColor={colors.textTertiary}
                                    value={note}
                                    onChangeText={setNote}
                                    multiline
                                    maxLength={2000}
                                    editable={!loading}
                                />
                                <Pressable
                                    onPress={confirmNoGps}
                                    disabled={loading || note.trim() === ""}
                                    style={styles.cancelButton}
                                >
                                    <Text
                                        style={[
                                            styles.noGpsProceedText,
                                            note.trim() === "" && styles.noGpsProceedDisabled
                                        ]}
                                    >
                                        Clock in without location
                                    </Text>
                                </Pressable>

                                <Pressable onPress={handleCancel} style={styles.cancelButton}>
                                    <Text style={styles.cancelText}>Cancel</Text>
                                </Pressable>
                            </>
                        ) : (
                            <>
                                <Text style={styles.title}>Ready to start your visit?</Text>

                                <Text style={styles.patientName}>
                                    {patient.firstName} {patient.lastName}
                                </Text>
                                <Text style={styles.address}>
                                    {patient.address ?? "Address not available"}
                                </Text>

                                <View style={styles.infoRow}>
                                    <Text style={styles.infoLabel}>Time</Text>
                                    <Text style={styles.infoValue}>{currentTime}</Text>
                                </View>

                                <View style={styles.infoRow}>
                                    <Text style={styles.infoLabel}>Location</Text>
                                    <Text
                                        style={[
                                            styles.infoValue,
                                            gpsStatus === "found" && styles.gpsFound,
                                            gpsStatus === "denied" && styles.gpsDenied
                                        ]}
                                    >
                                        {gpsStatus === "idle" && "Will verify on confirm"}
                                        {gpsStatus === "locating" &&
                                            (gpsAccuracyFt != null
                                                ? `Locating... (±${gpsAccuracyFt} ft)`
                                                : "Locating...")}
                                        {gpsStatus === "found" && "Location verified"}
                                        {gpsStatus === "denied" && "Location required"}
                                    </Text>
                                </View>

                                <Pressable
                                    onPress={runCapture}
                                    disabled={loading}
                                    style={({ pressed }) => [pressed && styles.pressed]}
                                >
                                    <LinearGradient
                                        colors={gradients.teal}
                                        start={{ x: 0, y: 0 }}
                                        end={{ x: 1, y: 1 }}
                                        style={[styles.confirmButton, shadows.teal]}
                                    >
                                        {loading ? (
                                            <ActivityIndicator color="#FFFFFF" />
                                        ) : (
                                            <Text style={styles.confirmText}>START VISIT</Text>
                                        )}
                                    </LinearGradient>
                                </Pressable>

                                <Pressable onPress={handleCancel} style={styles.cancelButton}>
                                    <Text style={styles.cancelText}>Cancel</Text>
                                </Pressable>
                            </>
                        )}
                    </ScrollView>
                </View>
            </View>
        </Modal>
    );
}

const styles = StyleSheet.create({
    backdrop: {
        flex: 1,
        backgroundColor: "rgba(31, 21, 71, 0.5)",
        justifyContent: "flex-end"
    },
    backdropTouch: {
        flex: 1
    },
    sheet: {
        backgroundColor: colors.surface,
        borderTopLeftRadius: radius["2xl"],
        borderTopRightRadius: radius["2xl"],
        padding: spacing.xl,
        paddingBottom: spacing["3xl"],
        ...shadows.xl
    },
    handle: {
        width: 40,
        height: 4,
        backgroundColor: colors.gray300,
        borderRadius: 2,
        alignSelf: "center",
        marginBottom: spacing.xl
    },
    // Grows with the content until `maxHeight` on the sheet caps it; past that
    // the sheet stops growing and this scrolls.
    sheetContent: {
        flexGrow: 1
    },
    title: {
        fontFamily: fonts.bold,
        fontSize: 18,
        color: colors.textPrimary,
        marginBottom: spacing.base
    },
    patientName: {
        fontFamily: fonts.semiBold,
        fontSize: 15,
        color: colors.textPrimary
    },
    address: {
        fontFamily: fonts.regular,
        fontSize: 13,
        color: colors.textSecondary,
        marginBottom: spacing.base
    },
    infoRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        paddingVertical: spacing.sm,
        borderBottomWidth: 1,
        borderBottomColor: colors.border
    },
    infoLabel: {
        fontFamily: fonts.medium,
        fontSize: 13,
        color: colors.textSecondary
    },
    infoValue: {
        fontFamily: fonts.semiBold,
        fontSize: 13,
        color: colors.textPrimary
    },
    gpsFound: {
        color: colors.success500
    },
    gpsDenied: {
        color: colors.warning500
    },
    farMessage: {
        fontFamily: fonts.regular,
        fontSize: 14,
        lineHeight: 20,
        color: colors.textSecondary,
        marginBottom: spacing.base
    },
    farDistance: {
        fontFamily: fonts.semiBold,
        color: colors.textPrimary
    },
    noGpsOrLabel: {
        fontFamily: fonts.medium,
        fontSize: 12,
        color: colors.textTertiary,
        textAlign: "center",
        marginTop: spacing.base,
        marginBottom: spacing.sm
    },
    noGpsProceedText: {
        fontFamily: fonts.semiBold,
        fontSize: 14,
        color: colors.warning600
    },
    noGpsProceedDisabled: {
        color: colors.textTertiary
    },
    /** View-level disabled state for the gradient buttons (the one above is text-only). */
    confirmButtonDisabled: {
        opacity: 0.4
    },
    noteInput: {
        minHeight: 72,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: radius.lg,
        padding: spacing.base,
        fontFamily: fonts.regular,
        fontSize: 14,
        color: colors.textPrimary,
        textAlignVertical: "top"
    },
    confirmButton: {
        height: 56,
        borderRadius: radius.full,
        alignItems: "center",
        justifyContent: "center",
        marginTop: spacing.xl
    },
    confirmText: {
        fontFamily: fonts.bold,
        fontSize: 14,
        color: "#FFFFFF",
        letterSpacing: 0.5
    },
    cancelButton: {
        alignItems: "center",
        paddingTop: spacing.base
    },
    cancelText: {
        fontFamily: fonts.medium,
        fontSize: 14,
        color: colors.textTertiary
    },
    pressed: {
        opacity: 0.9
    }
});
